import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ComboBoxItemType } from "./combobox";
import { cn } from "@/lib/utils";

type AutocompleteProps = {
  value?: string;
  onSelect: (value: string | undefined) => void;
  items: ComboBoxItemType[];
  searchPlaceholder?: string;
  noResultsMsg?: string;
  selectItemMsg?: string;
  className?: string;
  unselect?: boolean;
  unselectMsg?: string;
  onSearchChange?: (e: string) => void;
};

export default function Autocomplete({
  value,
  onSelect,
  items,
  searchPlaceholder = "Search item...",
  noResultsMsg = "No items found.",
  selectItemMsg = "Select an item...",
  className,
  onSearchChange,
}: AutocompleteProps) {
  const [selected, setSelected] = useState(
    items.find((item) => item.value === value),
  );
  const [query, setQuery] = useState("");

  const [filteredItems, setFilteredItems] = useState(items);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDropdownUp, setIsDropdownUp] = useState(false);

  const handleOnSearchChange = useDebouncedCallback((e: string) => {
    if (e === "") {
      setQuery("");
    }
    if (onSearchChange) {
      onSearchChange(e);
      setQuery(e);
    }
  }, 100);

  useEffect(() => {
    const filtered =
      query === ""
        ? items
        : items.filter(
            (item) =>
              item.label
                .toLowerCase()
                .replace(/\s+/g, "")
                .includes(query.toLowerCase().replace(/\s+/g, "")) ||
              item.value
                .toLowerCase()
                .replace(/\s+/g, "")
                .includes(query.toLowerCase().replace(/\s+/g, "")),
          );
    setFilteredItems(filtered);
  }, [items, query]);

  useEffect(() => {
    // Check the position of the input field and calculate space
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setIsDropdownUp(spaceBelow < 200); // Set threshold for switching to upward direction
    }
  }, [query]);

  return (
    <div className="">
      <Combobox
        value={selected ?? { label: "", value: "" }}
        onChange={(s) => {
          setSelected(
            s ?? {
              label: "",
              value: "",
            },
          );
          onSelect(s?.value);
        }}
        nullable
      >
        <div className="relative h-10">
          <div className="flex h-full w-full cursor-default items-center overflow-hidden rounded-sm bg-white text-left ring-1 sm:text-sm">
            <Combobox.Input
              ref={inputRef}
              className="h-full w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
              displayValue={(item: ComboBoxItemType) => item.value}
              onChange={(e) => handleOnSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              autoComplete="off"
              type="search"
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="ml-2 h-4 w-4 shrink-0 opacity-50"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              className={cn(
                "combobox-options-phg absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm",
                isDropdownUp ? "bottom-full mb-2" : "top-full",
              )}
            >
              {filteredItems.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  {noResultsMsg}
                </div>
              ) : (
                filteredItems.map((item) => (
                  <Combobox.Option
                    key={item.value}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-slate-400 text-white" : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`flex flex-col truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          <p>{item.label}</p>
                          <p
                            className={cn(
                              "text-xs",
                              active
                                ? "text-green-300"
                                : "text-muted-foreground",
                            )}
                          >
                            {item.value}
                          </p>
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
